import { apiUrl } from "./fetchData";
import pushNextPage from "./pushNextPage";
import { replacePage } from "./pageHistory";

// What went into a make, saved with it so that, if it's shared, someone else can
// remix it: questions and answers, style choices, context text and documents.
// Never the user's profile details or photos.
export function remixInputs(rootState) {
  const { questions, tone, format, length, language, output, context } =
    rootState;
  const question = ({ id, question: text, suggestedAnswers, fromContext }) => ({
    id,
    question: text,
    suggestedAnswers,
    ...(fromContext ? { fromContext: true } : {}),
  });
  return {
    questions: questions.questionList.map(question),
    contextQuestions: questions.contextQuestionList.map(question),
    answers: questions.chosenAnswers
      .filter((item) => item.options.length)
      .map((item) => ({ id: item.question.id, options: item.options })),
    tones: tone.toneList,
    tone: tone.activeTone,
    formats: format.formatList,
    format: format.activeFormat,
    languages: language.languageList,
    language: language.activeLanguage,
    length: String(length.selectedLength),
    lengthMoved: length.hasMoved,
    output: output.activeOutput,
    imageShape: output.imageShape,
    context: context.context,
    research: context.research,
    documents: context.documents.map(({ name, text, truncated }) => ({
      name,
      text,
      truncated,
    })),
  };
}

const list = (value) => (Array.isArray(value) ? value : []);
const text = (value) => (typeof value === "string" ? value : "");

function asQuestions(items) {
  return list(items)
    .filter(
      (item) =>
        item && Number.isInteger(item.id) && typeof item.question === "string"
    )
    .map((item) => ({
      id: item.id,
      question: item.question,
      suggestedAnswers: list(item.suggestedAnswers).filter(
        (answer) => typeof answer === "string"
      ),
      option: "",
      ...(item.fromContext ? { fromContext: true } : {}),
    }));
}

function asChoices(items, key) {
  return list(items)
    .filter((item) => item && typeof item[key] === "string")
    .map((item) => ({ [key]: item[key], emoji: text(item.emoji) }));
}

// Loads a shared make's inputs and result so the visitor carries on from it as
// their own new make. Resolves to true when it worked.
export async function startRemix(store, shareId, { replace = false } = {}) {
  let data;
  try {
    const response = await fetch(
      apiUrl(`shared/${encodeURIComponent(shareId)}/remix`)
    );
    if (!response.ok) return false;
    data = await response.json();
  } catch {
    return false;
  }
  const make = data?.make;
  if (!make || typeof make.prompt !== "string") return false;
  const inputs = data.inputs;
  const { commit, dispatch } = store;

  await dispatch("changePromptReset", make.prompt);
  commit("SET_JOURNEY_ID");

  if (inputs) {
    const questionList = asQuestions(inputs.questions);
    const contextQuestions = asQuestions(inputs.contextQuestions);
    const byId = new Map(
      [...questionList, ...contextQuestions].map((item) => [item.id, item])
    );
    commit("questions/SET_QUESTION_LIST", questionList);
    commit("questions/SET_CONTEXT_QUESTION_LIST", contextQuestions);
    commit(
      "questions/SET_CHOSEN_ANSWERS",
      list(inputs.answers)
        .filter((answer) => byId.has(answer?.id))
        .map((answer) => ({
          question: byId.get(answer.id),
          options: list(answer.options).filter(
            (option) => typeof option === "string"
          ),
        }))
    );
    commit("questions/SET_ID", Math.max(-1, ...[...byId.keys()]) + 1);

    const tones = asChoices(inputs.tones, "tone");
    commit("tone/SET_TONE_LIST", tones);
    commit("tone/SET_ACTIVE_TONE", text(inputs.tone));
    const formats = asChoices(inputs.formats, "format");
    commit("format/SET_FORMAT_LIST", formats);
    commit("format/SET_ACTIVE_FORMAT", text(inputs.format));
    const languages = asChoices(inputs.languages, "language");
    if (languages.length) commit("language/SET_LANGUAGE_LIST", languages);
    commit("language/SET_ACTIVE_LANGUAGE", text(inputs.language));
    if (inputs.lengthMoved) {
      commit("length/SET_SELECTED_LENGTH", text(inputs.length) || 2);
      commit("length/SET_HAS_MOVED", true);
    }
    dispatch("output/selectOutput", text(inputs.output) || "auto");
    dispatch("output/selectImageShape", text(inputs.imageShape) || "auto");

    commit("context/SET_CONTEXT", text(inputs.context));
    commit(
      "context/SET_DOCUMENTS",
      list(inputs.documents)
        .filter((document) => typeof document?.text === "string")
        .map((document) => ({
          name: text(document.name) || "Document",
          text: document.text,
          characters: document.text.length,
          truncated: Boolean(document.truncated),
        }))
    );
    commit("context/SET_RESEARCH", inputs.research === true);

    // Older shares may not have every list; fetch whatever is missing
    if (!questionList.length) dispatch("questions/getQuestions");
    if (!tones.length) dispatch("tone/getTones");
    if (!formats.length) dispatch("format/getFormats");
  } else {
    dispatch("getRefinements");
  }

  // The shared make becomes the start of the visitor's own
  await dispatch("result/showSavedMake", {
    id: "",
    prompt: make.prompt,
    text: text(make.text),
    output: text(make.output) || "text",
    image: text(make.image),
    sources: list(make.sources),
  });
  dispatch("drawer/createMake");
  if (replace) {
    replacePage(store.state);
  } else {
    pushNextPage(store.state);
  }
  return true;
}
